<template>
  <div class="apple-login-container">
      <!-- 非alleat域名直接显示webview -->
      <div v-if="shouldUseWebviewLogin" class="webview-container">
        <!-- 骨架屏加载状态 -->
        <div v-if="!webviewLoaded" class="webview-skeleton">
          <div class="skeleton-content">
            <div class="skeleton-icon"></div>
            <div class="skeleton-text"></div>
          </div>
        </div>
        <!-- webview iframe -->
        <iframe 
          v-show="webviewLoaded"
          :src="webviewUrl"
          class="webview-iframe"
          frameborder="0"
          @load="onWebviewLoad"
          @error="onWebviewError"
        ></iframe>
      </div>
    
    <!-- alleat域名显示按钮 -->
    <button 
      v-else
      class="apple-login-btn" 
      @click="handleAppleLogin"
      :disabled="loading"
    >
      <i v-if="loading" class="el-icon-loading"></i>
      <svg v-else class="apple-icon" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
      </svg>
      <span>{{ loading ? 'Signing in...' : 'Continue with Apple' }}</span>
    </button>
  </div>
</template>

<script>
import { signInWithPopup } from 'firebase/auth';
import { auth, appleProvider } from '@/firebase.js';
import { postGateway } from '@/request';
import bus from '@/utils/bus.js';
import { shouldUseWebviewLogin } from '@/utils/domain-helper.js';

// 检测是否为移动端设备
// function isMobileDevice() {
//   return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
// }

export default {
  name: 'AppleLogin',
  props: {
    fromPage: {
      type: String,
      default: null
    },
  },
  data() {
    return {
      loading: false,
      webviewUrl: '',
      webviewLoaded: false
    }
  },
  computed: {
    shouldUseWebviewLogin() {
      return shouldUseWebviewLogin();
    },
    isAndroid() {
      return /Android/i.test(navigator.userAgent);
    }
  },
  mounted() {
    // 如果是webview模式，准备webview URL
    if (this.shouldUseWebviewLogin) {
      this.setupWebview();
      // 监听来自webview的消息
      window.addEventListener('message', this.handleWebviewMessage);
    }
  },
  beforeDestroy() {
    // 清理事件监听器
    if (this.shouldUseWebviewLogin) {
      window.removeEventListener('message', this.handleWebviewMessage);
    }
  },
  methods: {
    async handleAppleLogin() {
      
      this.loading = true;
      
      try {
        await this.handleDirectLogin();
      } catch (error) {
        console.error('Apple login failed:', error);
        this.handleLoginError(error);
      } finally {
        this.loading = false;
      }
    },

    // 直接登录（alleatapp.com域名）
    async handleDirectLogin() {
      try {

        const result = await signInWithPopup(auth, appleProvider);
        console.log('Apple login result:', result);
        const user = result.user;
        
        console.log('Apple login successful:', user);
        
        // 检查邮箱是否存在，从多个来源获取邮箱
        let email = user.email;
        if (!email && user.providerData && user.providerData.length > 0) {
          email = user.providerData[0].email;
        }
        if (!email && user.reloadUserInfo && user.reloadUserInfo.providerUserInfo?.[0]?.email) {
          email = user.reloadUserInfo.providerUserInfo?.[0]?.email;
        }
        
        // Apple登录可能没有邮箱，这是正常的
        if (!email) {
          console.log('Apple login without email - this is normal for Apple Sign In');
          // 使用uid作为标识符
          email = user.uid + '@apple.privaterelay.appleid.com';
        }
        
        // 发送用户信息到后端
        await this.sendUserToBackend(user, email);
        
      } catch (error) {
        console.error('Apple direct login error:', error);
        this.handleLoginError(error);
      }
    },

    
    async sendUserToBackend(user, email) {
      try {
        // 获取Apple ID Token
        const idToken = await user.getIdToken();
        
        // 获取显示名称
        let displayName = user.displayName;
        if (!displayName && user.providerData && user.providerData.length > 0) {
          displayName = user.providerData[0].displayName;
        }
        
        const response = await postGateway({
          url: '/oauth/appGoogleLogin',
          method: 'POST',
          data: {
            idToken: idToken,
            email: email,
            firstName: displayName || 'Apple User',
            photoUrl: user.photoURL || '',
            appleId: user.uid,
            render: 'apple',
            type: 'web'
          }
        });
        
        if (response.code === 200 && response.data) {
          // 处理登录成功
          this.handleLoginSuccess(response.data);
          
        } else {
          throw new Error(response.msg || 'Login failed');
        }
        
      } catch (error) {
        console.error('Backend login failed:', error);
        this.$message.error(error.message || 'Login failed. Please try again.');
        throw error;
      }
    },
    
    // 处理登录成功
    async handleLoginSuccess(loginData) {
      // 保存登录信息
      if (loginData.token) {
        localStorage.setItem('token', loginData.token);
        localStorage.setItem('refreshToken', loginData.refreshToken);
        localStorage.setItem('clientId', loginData.clientId);
        localStorage.setItem('aeClient', loginData.clientId);
        localStorage.setItem('userInfo', JSON.stringify(loginData));
        
        // 通知header登录了
        bus.$emit('login');
        
        // 显示成功消息
        this.$message.success('Login successful!');
        
        // 检查是否需要绑定手机号
        const needBindPhone = await this.checkAndBindPhoneIfNeeded();
        
        // 如果不需要绑定手机号，完成登录流程
        if (!needBindPhone) {
          this.completeLogin();
        }
      }
    },
    
    // 完成登录流程
    completeLogin() {
      if (this.fromPage === 'checkout') {
        this.$emit('loginDone', true);
      } else if (this.fromPage === 'login') {
        this.$emit('loginDone', true);
      } else {
        // 默认跳转到首页
        location.href = '/';
      }
    },
    
    async checkAndBindPhoneIfNeeded() {
      try {
        // 通过用户详情接口获取最新的用户资料
        const detail = await this.fetchUserDetail();
        const contactPhone = (detail && detail.contactPhone) ? String(detail.contactPhone).trim() : '';
        
        if (!contactPhone) {
          // 需要绑定手机号
          if (this.fromPage === 'checkout') {
            // 在checkout页面时，发出事件让父组件处理
            this.$emit('needBindPhone');
          } else {
            // 其他页面跳转到绑定页面
            this.$router.replace({
              path: '/bind-phone',
              query: { from: this.fromPage }
            });
          }
          return true;
        }
        return false;
      } catch (error) {
        console.error('Check phone binding error:', error);
        return false;
      }
    },

    async fetchUserDetail() {
      const clientId = localStorage.getItem('clientId');
      if (!clientId) {
        return {};
      }
      const res = await postGateway({
        url: '/customerApp/center/getDetail',
        method: 'GET',
        data: {
          clientId
        }
      });
      return res && res.data ? res.data : {};
    },

    // 处理登录错误
    handleLoginError(error) {
      let errorMessage = 'Apple login failed. Please try again.';
      
      if (error.code === 'auth/account-exists-with-different-credential') {
        errorMessage = 'An account already exists with this email using a different login method.';
        this.$emit('needAlternativeLogin');
      } else if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Login was cancelled. Please try again.';
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = 'Popup was blocked. Please allow popups and try again.';
      } else if (error.code === 'auth/operation-not-allowed') {
        errorMessage = 'Apple Sign-In is not enabled. Please contact support.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      this.$message.error(errorMessage);
    },
    
    // 生成nonce用于Apple登录安全验证
    generateNonce() {
      const array = new Uint8Array(32);
      crypto.getRandomValues(array);
      return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
    },
    
    // 设置webview
    setupWebview() {
      const baseUrl = "https://alleatapp.com";
      const isMobile = window.innerWidth < 950;
      this.webviewUrl = `${baseUrl}/firebase-login-webview?type=apple&embedded=true&mobile=${isMobile}`;
    },
    
    
    // webview加载完成
    onWebviewLoad() {
      console.log('Apple login webview loaded');
    },
    
    // webview加载错误
    onWebviewError() {
      console.error('Apple login webview failed to load');
      this.$message.error('Failed to load Apple login. Please try again.');
    },
    
    // 处理来自webview的消息
    handleWebviewMessage(event) {
      // 允许跨域通信，但验证消息格式
      if (!event.data || typeof event.data !== 'object') {
        return;
      }
      
      // 处理webview加载完成消息
      if (event.data.type === 'WEBVIEW_LOADED' && event.data.loginType === 'apple') {
        console.log('Apple webview loaded successfully');
        this.webviewLoaded = true;
        return;
      }
      
      // 只处理Apple登录相关的消息
      if (event.data.type === 'LOGIN_SUCCESS' && event.data.loginType === 'apple') {
        console.log('Apple login success from webview:', event.data);
        this.loading = false;
        this.handleLoginSuccess(event.data.data);
      } else if (event.data.type === 'LOGIN_ERROR' && event.data.loginType === 'apple') {
        console.error('Apple login error from webview:', event.data.error);
        this.loading = false;
        this.$message.error(event.data.error || 'Apple login failed');
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.apple-login-container {
  width: 100%;
  display: flex;
  justify-content: center;
  border-radius: 8px;
  overflow: hidden;
}

.apple-login-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 300px;
  height: 48px;
  background: #000;
  border: 1px solid #000;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #fff;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  
  &:hover:not(:disabled) {
    background: #333;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
    transform: translateY(-1px);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
  
  .apple-icon {
    width: 20px;
    height: 20px;
    margin-right: 12px;
    fill: currentColor;
  }
  
  .el-icon-loading {
    margin-right: 8px;
    font-size: 16px;
  }
}

// Webview样式
.webview-container {
  width: 100%;
  height: 56px; /* 与按钮高度一致 */
  overflow: hidden;
  position: relative;
  
  .webview-iframe {
    width: 100%;
    height: 100%;
    border: none;
  }
  
  .webview-skeleton {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f8f8f8;
    border-radius: 8px;
    
    .skeleton-content {
      display: flex;
      align-items: center;
      width: 100%;
      max-width: 300px;
      padding: 0 16px;
      
      .skeleton-icon {
        width: 20px;
        height: 20px;
        background: #d0d0d0;
        border-radius: 4px;
        margin-right: 12px;
        animation: skeleton-pulse 2.5s ease-in-out infinite;
      }
      
      .skeleton-text {
        flex: 1;
        height: 16px;
        background: #d0d0d0;
        border-radius: 4px;
        animation: skeleton-pulse 2.5s ease-in-out infinite;
      }
    }
  }
}

// 移动端适配
@media (max-width: 480px) {
  .apple-login-btn {
    height: 44px;
    font-size: 13px;
    
    .apple-icon {
      width: 18px;
      height: 18px;
      margin-right: 10px;
    }
  }
  
  .webview-container {
    height: 44px; /* 移动端与按钮高度一致 */
    
    .webview-skeleton {
      .skeleton-content {
        .skeleton-icon {
          width: 18px;
          height: 18px;
        }
        
        .skeleton-text {
          height: 14px;
        }
      }
    }
  }
}

@keyframes skeleton-pulse {
  0% {
    opacity: 0.5;
  }
  50% {
    opacity: 0.2;
  }
  100% {
    opacity: 0.5;
  }
}
</style>
